import React from "react";
import { Table } from "antd";
import { FileTextOutlined, CloseCircleOutlined, DollarOutlined, DeleteOutlined } from "@ant-design/icons";
import { numberWithCommas } from "utils/GlobalFunctions";
import { showConfirmDlgWidget } from "components/ConfirmDlgWidget/showConfirmDlgWidget";
import moment from "moment";
import "./RegistrationsTable.css";

function RegistrationsTable(props) {
  const { dataList, onRowClick, onMarkAsPaid, onInvoice, onVoid, onRefund, onDelete, merchant, columnPreferences } = props;

  if (dataList === undefined) return <div />;

  const formatDateShort = (value) => {
    if (!value) return "";
    const parsed = moment(value, ["YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss", moment.ISO_8601], true);
    return parsed.isValid() ? parsed.format("MM/DD/YY") : value;
  };

  const columns = [
    {
      title: "Trans ID",
      dataIndex: "transaction_id",
      key: "transaction_id",
      width: 90,
      fixed: "left",
      sorter: (a, b) => parseInt(a.transaction_id) - parseInt(b.transaction_id)
    },
    {
      title: "Parent First",
      dataIndex: "parent_first",
      key: "parent_first",
      width: 120,
      sorter: (a, b) => `${a.parent_first}`.localeCompare(`${b.parent_first}`)
    },
    {
      title: "Parent Last",
      dataIndex: "parent_last",
      key: "parent_last",
      width: 120,
      sorter: (a, b) => `${a.parent_last}`.localeCompare(`${b.parent_last}`)
    },
    {
      title: "Email",
      dataIndex: "p_email",
      key: "p_email",
      width: 200,
      sorter: (a, b) => `${a.p_email}`.localeCompare(`${b.p_email}`)
    },
    {
      title: "Cell Phone",
      dataIndex: "p_cell_phone",
      key: "p_cell_phone",
      width: 130
    },
    {
      title: "Participants",
      dataIndex: "participant_count",
      key: "participant_count",
      width: 100,
      align: "center",
      sorter: (a, b) => parseInt(a.participant_count) - parseInt(b.participant_count)
    },
    {
      title: "Programs",
      dataIndex: "program_count",
      key: "program_count",
      width: 100,
      align: "center",
      sorter: (a, b) => parseInt(a.program_count) - parseInt(b.program_count)
    },
    {
      title: "Total",
      dataIndex: "total_checkout",
      key: "total_checkout",
      width: 100,
      sorter: (a, b) => parseFloat(a.total_checkout) - parseFloat(b.total_checkout),
      render: (info) => {
        return "$" + numberWithCommas(info);
      }
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      width: 130,
      sorter: (a, b) => `${a.payment_method}`.localeCompare(`${b.payment_method}`)
    },
    {
      title: "Discount Code",
      dataIndex: "discount_code",
      key: "discount_code",
      width: 120,
      sorter: (a, b) => `${a.discount_code || ""}`.localeCompare(`${b.discount_code || ""}`)
    },
    {
      title: "Reg Date",
      dataIndex: "reg_date",
      key: "reg_date",
      width: 110,
      sorter: (a, b) => `${a.reg_date}`.localeCompare(`${b.reg_date}`),
      render: (record) => formatDateShort(record)
    }
  ];

  // Insert optional columns based on user preferences
  if (columnPreferences?.columns?.a_park_id?.visible) {
    const regDateIdx = columns.findIndex(c => c.key === "reg_date");
    columns.splice(regDateIdx, 0, {
      title: "Park ID",
      dataIndex: "a_park_id",
      key: "a_park_id",
      width: 100,
      sorter: (a, b) => `${a.a_park_id || ""}`.localeCompare(`${b.a_park_id || ""}`)
    });
  }

  // Add Actions column if any action handlers are provided
  if (onMarkAsPaid || onInvoice || onVoid || onRefund || onDelete) {
    columns.push({
      title: "Actions",
      key: "actions",
      width: 280,
      render: (_, record) => {
        const registrationMerchant = (merchant || '').toLowerCase();
        // Fall back to transaction hints when merchant is unknown
        const paymentMethod = (record.payment_method || '').toLowerCase();
        const paymentGateway = (record.payment_gateway || '').toLowerCase();
        
        // Check if this transaction supports void/refund based on merchant
        const hasKnownMerchant = registrationMerchant === 'forte' || registrationMerchant === 'stax';
        const canProcessTransaction = hasKnownMerchant || (!registrationMerchant && (paymentGateway === 'forte' || paymentGateway === 'stax' || paymentMethod === 'credit'));

        // Prefer created_at for same-day check, fall back to reg_date when missing
        const rawDate = record.created_at || record.reg_date;
        const transactionDate = rawDate ? new Date(rawDate) : null;
        const today = new Date();
        const isSameDay = transactionDate && 
          transactionDate.getFullYear() === today.getFullYear() &&
          transactionDate.getMonth() === today.getMonth() &&
          transactionDate.getDate() === today.getDate();
        
        // Only show void for same-day transactions (both Forte and Stax)
        const canVoid = canProcessTransaction && isSameDay && onVoid;
        // Show refund for all supported transactions
        const canRefund = canProcessTransaction && onRefund;

        return (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {onInvoice && (
              <a
                style={{ color: "#1890ff" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onInvoice(record.transaction_id, record, onMarkAsPaid ? "invoice" : "receipt");
                }}
              >
                <FileTextOutlined /> {onMarkAsPaid ? "Invoice" : "Receipt"}
              </a>
            )}
            {onMarkAsPaid && (
              <a
                style={{ color: "#52c41a" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkAsPaid(record.transaction_id);
                }}
              >
                Mark as Paid
              </a>
            )}
            {canVoid && (
              <a
                style={{ color: "#ff4d4f" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onVoid(record);
                }}
              >
                <CloseCircleOutlined /> Void
              </a>
            )}
            {canRefund && (
              <a
                style={{ color: "#ff7a00" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRefund(record);
                }}
              >
                <DollarOutlined /> Refund
              </a>
            )}
            {onDelete && (
              <a
                style={{ color: "red" }}
                onClick={async (e) => {
                  e.stopPropagation();
                  var res = await showConfirmDlgWidget({
                    title: "Delete Confirmation",
                    content: "Are you sure you want to delete this transaction and its participants?",
                  });
                  if (res === false) return;
                  onDelete(record.transaction_id);
                }}
              >
                <DeleteOutlined /> Delete
              </a>
            )}
          </div>
        );
      }
    });
  }

  const dataSource =
    dataList.length === 0
      ? []
      : dataList.map((data, index) => {
          return { ...data, key: index };
        });

  return (
    <div className="info-list-widget">
      <Table
        columns={columns}
        dataSource={dataSource}
        rowClassName={(record, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
        onRow={(record) => ({
          onClick: () => onRowClick && onRowClick(record),
          style: onRowClick ? { cursor: 'pointer' } : {}
        })}
        scroll={{ x: 1400 }}
        pagination={{ pageSize: 10 }}
      />
    </div>
  );
}

export default RegistrationsTable;
